import {
  AlphabetAndNumberRegex,
  getMaxLength,
  IdentifierType,
  IdentifierTypeValue,
  NavigateConst,
  OnlyNumbers,
} from 'common';
import { isAvMed } from 'context';

export const isDrRegistrationFlow = (
  pathname: string,
  isDesignatedRepresentativeEnabled?: boolean,
) =>
  Boolean(isDesignatedRepresentativeEnabled) &&
  (pathname === NavigateConst.drRegistration ||
    pathname === NavigateConst.childOnlyPlanDesignatedRep);

export const isChildOnlyDrPersonalInformationStep = (
  pathname: string,
  page: number,
) => pathname === NavigateConst.childOnlyPlanDesignatedRep && isAvMed() && page === 1;

export const isTinyUrlDrFlow = (proxyAuthorizationLinkData?: {
  forgerockUuid?: string;
  correlationId?: string;
}) =>
  Boolean(
    proxyAuthorizationLinkData?.forgerockUuid &&
      proxyAuthorizationLinkData?.correlationId,
  );

export const isNoContentResponse = (response: any) => {
  const status = response?.status ?? response?.response?.status ?? response?.response?.statusCode;
  const errorCode = response?.errorCode ?? response?.response?.errorCode;

  return status === 204 || errorCode === 204;
};

export const shouldRedirectDrToGuestRegistration = (
  response: any,
  proxyAuthorizationLinkData?: {
    forgerockUuid?: string;
    correlationId?: string;
  },
  isDrFlow = false,
) => isDrFlow && isNoContentResponse(response) && isTinyUrlDrFlow(proxyAuthorizationLinkData);

export const getDrGuestRegistrationPath = () =>
  `${NavigateConst.registration}?guest=true`;

export const getDrIdentifierOptions = (
  t: (key: string) => string,
): Array<{
  value: IdentifierTypeValue;
  label: string;
  fieldKey: string;
}> =>
  isAvMed()
    ? [
        {
          value: IdentifierType.SSN,
          label: t('activation.socialSecurityNumber'),
          fieldKey: 'ssn',
        },
      ]
    : [
        {
          value: IdentifierType.MEDICARE_ID,
          label: t('activation.medicareIdLabel'),
          fieldKey: 'medicareId',
        },
        {
          value: IdentifierType.MEDICAID_ID,
          label: t('activation.medicaIdLabel'),
          fieldKey: 'medicaId',
        },
        {
          value: IdentifierType.SSN,
          label: t('activation.socialSecurityNumber'),
          fieldKey: 'ssn',
        },
      ];

export const getDrIdentifierFieldConfig = (t: (key: string) => string) => ({
  [IdentifierType.MEMBER_ID]: {
    id: 'memberId',
    label: t('activation.memberId'),
    placeholder: t('activation.memberIdPlaceholder'),
    maxLength: getMaxLength.memberId,
    errorMessage: t('activation.memberIdError'),
    restrictInput: AlphabetAndNumberRegex,
    tooltipText: t('activation.memberIdTooltipText'),
  },
  [IdentifierType.SSN]: {
    id: 'ssn',
    label: t('activation.socialSecurityNumber'),
    placeholder: t('common.ssnPlaceHolder'),
    maxLength: 4,
    minLength: 4,
    errorMessage: t('common.ssnRequired'),
    restrictInput: OnlyNumbers,
    tooltipText: t('activation.ssnTooltipText'),
  },
  [IdentifierType.MEDICARE_ID]: {
    id: 'medicareId',
    label: t('activation.medicareIdLabel'),
    placeholder: t('activation.medicareIdPlaceholder'),
    maxLength: getMaxLength.memberId,
    errorMessage: t('activation.medicareIdError'),
    restrictInput: AlphabetAndNumberRegex,
    tooltipText: t('activation.medicareIdTooltipText'),
  },
  [IdentifierType.MEDICAID_ID]: {
    id: 'medicaId',
    label: t('activation.medicaIdLabel'),
    placeholder: t('activation.medicaIdPlaceholder'),
    maxLength: getMaxLength.memberId,
    errorMessage: t('activation.medicaidIdError'),
    restrictInput: AlphabetAndNumberRegex,
    tooltipText: t('activation.medicaidIdTooltipText'),
  },
});

export const parseDrVerifyFailureReason = (
  response: any,
  fallbackReason: string,
) =>
  response?.error?.detail ||
  response?.error?.title ||
  response?.errorCode ||
  fallbackReason;

export const getDrVerifyRequest = (
  proxyAuthorizationLinkData: {
    forgerockUuid?: string;
    correlationId?: string;
  } | undefined,
  data: {
    firstName: string;
    lastName: string;
    email: string;
  },
  formattedDate: string,
) => ({
  identifier: [
    {
      system: 'designatorLoginId',
      value: proxyAuthorizationLinkData?.forgerockUuid ?? '',
    },
    {
      system: 'correlationId',
      value: proxyAuthorizationLinkData?.correlationId ?? '',
    },
  ],
  firstName: data.firstName,
  lastName: data.lastName,
  dateOfBirth: formattedDate,
  emailAddress: data.email,
  personaType: 'Caregiver',
});

export const getThrownErrorMessage = (
  error: unknown,
  fallbackReason: string,
) =>
  (error && typeof error === 'object' && 'message' in error
    ? String((error as { message?: unknown }).message ?? '')
    : '') || fallbackReason;
